/**
 * @file     mortgageCalculatorFlow.ts
 * @readme   ../../CLAUDE.md
 * @purpose  English strings for the mortgage calculator flow: step labels, helper text, input labels, results copy, CTA fork, and lead capture.
 */
export const mortgageCalculatorFlow = {
  seo: {
    title: 'Vancouver mortgage calculator | See what your monthly payment actually looks like',
    description:
      'Run your own numbers for a Vancouver home purchase. Monthly breakdown, CMHC insurance, property tax, and what it means for your real budget.',
    ogTitle: 'Vancouver mortgage calculator',
    ogDescription:
      'Run your numbers, see the full monthly picture, and decide your next step with clarity.'
  },
  page: {
    title: 'Mortgage calculator',
    description:
      'Run your own numbers before anyone else runs them for you. See the full monthly cost, not just the mortgage payment - and decide whether the next step is solo research or a structured buyer plan.'
  },
  actions: {
    next: 'Next',
    back: 'Back',
    calculate: 'See your numbers',
    skipUseEstimates: 'Skip and use Vancouver estimates',
    startOver: 'Start over with new numbers'
  },
  homePrice: {
    title: 'What price range are you looking at?',
    description:
      'Start with the neighbourhood and home type you have in mind, not what a pre-approval letter says you can stretch to.',
    label: 'Home price',
    placeholder: '850,000'
  },
  downPayment: {
    title: 'How much are you putting down?',
    description:
      'Below 20% triggers CMHC mortgage insurance in Canada. It gets added to your loan balance. Worth knowing before you commit to a number.',
    amountLabel: 'Down payment amount',
    amountPlaceholder: '170,000',
    percentLabel: 'Down payment percentage',
    cmhcNote: 'Below 20%. CMHC insurance will apply',
    cmhcExplain: 'CMHC insurance protects the lender when your down payment is under 20%. The premium is added to your mortgage balance and spread across your payments.'
  },
  loanTerm: {
    title: 'How long do you want to spread the payments?',
    description:
      'A longer term means lower monthly payments but more total interest. Most Vancouver buyers choose 25 years. 30 is available if you need more breathing room.',
    twentyFive: '25 years',
    twentyFiveDescription: 'Standard amortization, lower total interest, higher monthly payment.',
    thirty: '30 years',
    thirtyDescription: 'More monthly flexibility, but you pay significantly more interest over the life of the loan.'
  },
  interestRate: {
    title: 'What rate are you working with?',
    description:
      'If you have a pre-approval, use that rate. Otherwise the default reflects current posted rates - your actual rate will depend on your credit profile and lender.',
    label: 'Annual interest rate',
    placeholder: '5.24',
    currentRateNote: 'Reflects approximate posted rates as of early 2026. Your rate may differ.'
  },
  propertyCosts: {
    title: 'Monthly property costs beyond the mortgage',
    description:
      'These add up faster than most buyers expect. Vancouver property tax, home insurance, and strata fees can shift your real monthly number by hundreds of dollars.',
    taxLabel: 'Monthly property tax',
    taxPlaceholder: '350',
    insuranceLabel: 'Monthly home insurance',
    insurancePlaceholder: '125',
    hoaLabel: 'Monthly strata / HOA fees',
    hoaPlaceholder: '400',
    estimateNote: 'Defaults are rough Vancouver medians. Adjust if you have specifics.'
  },
  results: {
    title: 'Here is what your monthly commitment looks like',
    monthlyTotal: 'Total monthly cost',
    principalAndInterest: 'Principal & interest',
    propertyTax: 'Property tax',
    homeInsurance: 'Home insurance',
    strataHoa: 'Strata / HOA',
    cmhcInsurance: 'CMHC insurance (added to balance)',
    totalCostOverLife: 'Total cost over the full term',
    totalInterestPaid: 'Total interest paid',
    effectiveDownPayment: 'Effective down payment',
    contextHeadline: 'What do these numbers mean for your life?',
    contextBody:
      'A monthly number only matters in context. Compare it to what you pay now, what you spend on non-housing priorities, and how much flexibility you want to keep. The goal is a payment that feels durable, not one that technically qualifies.',
    cmhcAppliedNote: 'CMHC mortgage insurance has been added to your loan balance because your down payment is under 20%.'
  },
  ctaFork: {
    title: 'Now that you have the numbers',
    helpPath: {
      label: 'Build a buyer plan with Ray',
      description:
        'Turn these numbers into a search strategy, with neighbourhood fit, property types, and trade-offs mapped to your actual budget. Property Match takes about three minutes.'
    },
    selfPath: {
      label: 'Keep exploring on your own',
      description:
        'You have the numbers. Bookmark this page, adjust the inputs, and come back when you are ready to move forward.'
    }
  },
  leadCapture: {
    title: 'A few details so your buyer plan is grounded in real numbers',
    description:
      'The calculator gives you a starting point. Property Match turns it into a search strategy, with neighbourhood fit, budget trade-offs, and next steps you can actually use.',
    nameLabel: 'Name',
    namePlaceholder: 'Your full name',
    emailLabel: 'Email',
    emailPlaceholder: 'name@email.com',
    phoneLabel: 'Phone (optional)',
    phonePlaceholder: '(604) 555-0123',
    submit: 'Start your buyer plan',
    privacyNote: 'Your information stays between you and Ray. No mailing lists, no third parties.',
    success:
      'Thanks {{name}}. Your calculator inputs and contact details have been received. A buyer plan grounded in your numbers will follow shortly.'
  },
  progress: {
    label: 'Step {{current}} of {{total}}'
  }
} as const;
